import { useEffect, useMemo, useState } from 'react';
import { apiClient } from '../../../auth-lib';
import { Select, Button } from '../../../design-system';
import { useReport, ReportCard, Legend } from './reportsKit';

// Tally-style filing grid: clients (rows) × periods (cols) for one return type.
// Cells read F (filed) / P (pending) / N (not applicable), colour-coded, with
// filed-late in amber and overdue in red. Rows are selectable (checkboxes),
// filterable by status/search, and the selection exports to CSV.
const BUCKET = {
  filed: { ch: 'F', cls: 'ftrk-filed', label: 'Filed' },
  late: { ch: 'F', cls: 'ftrk-late', label: 'Filed late' },
  pending: { ch: 'P', cls: 'ftrk-pending', label: 'Pending' },
  overdue: { ch: 'P', cls: 'ftrk-overdue', label: 'Overdue' },
  na: { ch: 'N', cls: 'ftrk-na', label: 'Not applicable' },
};

const STATUS_FILTERS = [
  { value: 'all', label: 'All rows' },
  { value: 'pending', label: 'Has pending' },
  { value: 'overdue', label: 'Has overdue' },
  { value: 'late', label: 'Has filed-late' },
  { value: 'filed', label: 'Has filed' },
  { value: 'na', label: 'Has N/A' },
];

const fyLabel = (y) => `FY ${y}-${String((y + 1) % 100).padStart(2, '0')}`;
const rowKey = (r) => `${r.clientId}|${r.gstin}`;
const csvCell = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;

function FilingTrackerReports() {
  const [types, setTypes] = useState([]);
  const [typeId, setTypeId] = useState('');
  const [fy, setFy] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selected, setSelected] = useState(() => new Set());

  useEffect(() => {
    apiClient
      .get('/api/compliance-types')
      .then((r) => {
        const list = r.data || [];
        setTypes(list);
        setTypeId((prev) => prev || (list[0]?._id ?? ''));
      })
      .catch(() => setTypes([]));
  }, []);

  const { data, loading, error } = useReport('filing-tracker', { typeId, ...(fy ? { fy } : {}) });

  const columns = data?.columns || [];
  const rows = data?.rows || [];
  const totals = data?.totals || {};
  const availableFYs = data?.availableFYs || [];

  useEffect(() => {
    if (fy === '' && data?.fy != null) setFy(String(data.fy));
  }, [data, fy]);

  // Changing the return type or year is a different dataset — drop the selection.
  const resetSelection = () => setSelected(new Set());

  const visibleRows = useMemo(() => {
    let list = rows;
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (r) =>
          r.name?.toLowerCase().includes(q) ||
          (r.gstin || '').toLowerCase().includes(q) ||
          (r.code || '').toLowerCase().includes(q)
      );
    }
    if (statusFilter !== 'all') {
      list = list.filter((r) => Object.values(r.cells).some((b) => b === statusFilter));
    }
    return list;
  }, [rows, search, statusFilter]);

  const allVisibleSelected = visibleRows.length > 0 && visibleRows.every((r) => selected.has(rowKey(r)));

  const toggleRow = (key) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) visibleRows.forEach((r) => next.delete(rowKey(r)));
      else visibleRows.forEach((r) => next.add(rowKey(r)));
      return next;
    });
  };

  const exportCsv = () => {
    const chosen = selected.size
      ? visibleRows.filter((r) => selected.has(rowKey(r)))
      : visibleRows;
    if (chosen.length === 0) return;
    const header = ['Client', 'Code', 'GSTIN', ...columns.map((c) => c.label)];
    const body = chosen.map((r) => [
      r.name,
      r.code,
      r.gstin,
      ...columns.map((c) => (r.cells[c.key] ? BUCKET[r.cells[c.key]].label : '')),
    ]);
    const csv = [header, ...body].map((line) => line.map(csvCell).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `filing-tracker-${data?.type?.code || 'return'}-FY${data?.fy ?? ''}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="rep-grid-1">
      <ReportCard
        title="Return Filing Tracker"
        subtitle="Filing status for every client across the year — pick a return type"
        icon="🗓️"
        loading={loading}
        error={error}
        action={
          <div className="ftrk-controls">
            <Select
              value={typeId}
              onChange={(e) => { setTypeId(e.target.value); setFy(''); resetSelection(); }}
              aria-label="Return type"
            >
              {types.length === 0 && <option value="">Loading…</option>}
              {types.map((t) => (
                <option key={t._id} value={t._id}>{t.name}</option>
              ))}
            </Select>
            <Select
              value={fy}
              onChange={(e) => { setFy(e.target.value); resetSelection(); }}
              aria-label="Financial year"
              disabled={availableFYs.length === 0}
            >
              {availableFYs.length === 0 && <option value="">—</option>}
              {availableFYs.map((y) => (
                <option key={y} value={y}>{fyLabel(y)}</option>
              ))}
            </Select>
          </div>
        }
      >
        <Legend
          items={[
            { label: 'Filed', color: 'var(--ds-success-600)' },
            { label: 'Filed late', color: 'var(--ds-warning-600)' },
            { label: 'Pending', color: 'var(--ds-neutral-400)' },
            { label: 'Overdue', color: 'var(--ds-danger-600)' },
            { label: 'N/A', color: 'var(--ds-neutral-300)' },
          ]}
        />

        {/* Select / filter / export toolbar (Tally-style). */}
        <div className="ftrk-toolbar">
          <input
            className="ftrk-search"
            placeholder="🔎 Filter by client, code or GSTIN…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} aria-label="Filter by status">
            {STATUS_FILTERS.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </Select>
          <span className="ftrk-selcount">
            {selected.size > 0 ? `${selected.size} selected` : `${visibleRows.length} shown`}
          </span>
          <span style={{ flex: 1 }} />
          {selected.size > 0 && (
            <Button variant="secondary" size="sm" onClick={resetSelection}>Clear</Button>
          )}
          <Button variant="secondary" size="sm" onClick={exportCsv} disabled={visibleRows.length === 0}>
            Export {selected.size > 0 ? 'selected' : 'CSV'}
          </Button>
        </div>

        {visibleRows.length === 0 ? (
          <p className="rep-empty">
            {rows.length === 0 ? 'No compliance instances for this return type / year yet.' : 'No rows match the filter.'}
          </p>
        ) : (
          <div className="ftrk-scroll">
            <table className="ftrk-table">
              <thead>
                <tr>
                  <th className="ftrk-sticky ftrk-th-name">
                    <label className="ftrk-check-row">
                      <input type="checkbox" checked={allVisibleSelected} onChange={toggleAll} aria-label="Select all" />
                      Client
                    </label>
                  </th>
                  <th className="ftrk-th-gstin">GSTIN</th>
                  {columns.map((c) => (
                    <th key={c.key} className="ftrk-th-col">{c.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((r) => {
                  const key = rowKey(r);
                  const isSel = selected.has(key);
                  return (
                    <tr key={key} className={isSel ? 'ftrk-row-sel' : ''}>
                      <td className="ftrk-sticky ftrk-td-name">
                        <label className="ftrk-check-row">
                          <input type="checkbox" checked={isSel} onChange={() => toggleRow(key)} aria-label={`Select ${r.name}`} />
                          <span>
                            <span className="ftrk-name">{r.name}</span>
                            {r.code && <span className="ftrk-code">{r.code}</span>}
                          </span>
                        </label>
                      </td>
                      <td className="ftrk-td-gstin">{r.gstin || '—'}</td>
                      {columns.map((c) => {
                        const meta = r.cells[c.key] ? BUCKET[r.cells[c.key]] : null;
                        return (
                          <td key={c.key} className="ftrk-cell">
                            {meta ? (
                              <span className={`ftrk-badge ${meta.cls}`} title={meta.label}>{meta.ch}</span>
                            ) : (
                              <span className="ftrk-empty" title="No period">·</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {rows.length > 0 && (
          <div className="ftrk-totals">
            {rows.length} total · Filed {totals.filed || 0} · Late {totals.late || 0}
            {' '}· Pending {totals.pending || 0} · Overdue {totals.overdue || 0} · N/A {totals.na || 0}
          </div>
        )}
      </ReportCard>
    </div>
  );
}

export default FilingTrackerReports;
